import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import type { ReactElement, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { reasonMessage } from '../../../i18n';
import type { ScreenFailure } from '../channel-selection/screen-failure';

// A constant, not a text: the i18n lint rule flags string attributes.
const STATUS_ROLE = 'status';
// Focus can be moved to the alert when a result arrives (R31) without it joining the tab order.
const FOCUS_ONLY = -1;

/** Props of {@link FailureAlert}. */
export interface FailureAlertProps {
  readonly failure: ScreenFailure;
  /** `error` is announced at once (`role="alert"`); `warning` is a status (R8: not a failure). */
  readonly tone?: 'error' | 'warning';
  /** A bold line above the text (R9 read-only title), already translated. */
  readonly title?: string | undefined;
  /** Receives focus when a result arrives (R31). */
  readonly alertRef?: Ref<HTMLDivElement> | undefined;
}

/**
 * The catalogue text of a failure or a warning of E4 (I12, R20): never a problem title, detail or
 * broker message. The same alert as E3's `ReasonText`, plus a title, a warning tone and a focus ref.
 */
export function FailureAlert({
  failure,
  tone = 'error',
  title,
  alertRef,
}: FailureAlertProps): ReactElement {
  const { t } = useTranslation();
  const message = reasonMessage(failure.reasonCode, failure.params);
  return (
    <Alert
      severity={tone}
      role={tone === 'warning' ? STATUS_ROLE : undefined}
      ref={alertRef}
      tabIndex={FOCUS_ONLY}
    >
      {title === undefined ? null : <AlertTitle>{title}</AlertTitle>}
      {t(message.key, message.values)}
    </Alert>
  );
}
